import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { DateObjectType } from '@typings/common';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useErrors } from '@contexts/ErrorsContext';
import { getErrorMessage } from '@utils/commons';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles.scss';

dayjs.extend(customParseFormat);

export interface ReviewDateType {
  dateDetails: DateObjectType;
  isNotRelevant: boolean;
}

interface ReviewDateProps {
  reviewDateFromAPI: string;
  reviewDate: ReviewDateType;
  setReviewDate: React.Dispatch<React.SetStateAction<ReviewDateType>>;
}

const ReviewDate: React.FC<ReviewDateProps> = ({
  reviewDateFromAPI,
  reviewDate,
  setReviewDate,
}) => {
  const [reviewDateErrorMessage, setReviewDateErrorMessage] = useState<
    string | undefined
  >('');
  const { errors } = useErrors();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof reviewDateFromAPI === 'string') {
      if (reviewDateFromAPI.toLowerCase() === 'not relevant') {
        setReviewDate(prev => ({ ...prev, isNotRelevant: true }));
      } else {
        const parsedDate = dayjs(reviewDateFromAPI, 'YYYY-MM-DD', true); // Parse the date in strict mode
        if (parsedDate.isValid()) {
          setReviewDate(prev => ({
            ...prev,
            dateDetails: {
              day: parsedDate.format('DD'),
              month: parsedDate.format('MM'),
              year: parsedDate.format('YYYY'),
            },
          }));
        } else {
          // If the date is invalid, reset to an empty state
          setReviewDate(prev => ({
            ...prev,
            dateDetails: {
              day: '',
              month: '',
              year: '',
            },
          }));
        }
      }
    }
  }, [reviewDateFromAPI, setReviewDate]);

  useEffect(() => {
    setReviewDateErrorMessage(getErrorMessage(errors, 'reviewDate'));
  }, [errors]);

  const onClickingNotRelevant = (e: any) => {
    const isChecked = e.target.checked;
    setReviewDate(prev => ({ ...prev, isNotRelevant: isChecked }));
    if (isChecked) {
      setReviewDate(prev => ({
        ...prev,
        dateDetails: {
          day: '',
          month: '',
          year: '',
        },
      }));
    }
  };

  useEffect(() => {
    const isAnyFieldFilled =
      reviewDate.dateDetails.day.trim() ||
      reviewDate.dateDetails.month.trim() ||
      reviewDate.dateDetails.year.trim();
    if (isAnyFieldFilled) {
      setReviewDate({ ...reviewDate, isNotRelevant: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewDate.dateDetails]);

  useEffect(() => {
    const scrollToId = (location.state as any)?.scrollTo;
    if (scrollToId) {
      setTimeout(() => {
        const el = document.getElementById(scrollToId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Clear the scroll state to prevent it from running again
        navigate(location.pathname, { replace: true, state: {} });
      }, 50);
    }
  }, [location, navigate]);

  return (
    <div className="my-5 date-range" id="acpReviewDateForm">
      <h1 className="govuk-heading-l">Advance Care Plan - Review Date</h1>
      <div
        className={`govuk-form-group mt-10 govuk-bp:mt-14 ${reviewDateErrorMessage ? 'govuk-form-group--error' : ''}`}
      >
        <fieldset
          className="govuk-fieldset"
          role="group"
          aria-describedby="review-date-hint"
        >
          <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
            <h3 className="govuk-fieldset__heading">
              When should this Advance Care Plan be revisited and reviewed?
            </h3>
          </legend>
          <div id="review-date-hint" className="govuk-hint">
            For example, 15 5 2024
          </div>
          {reviewDateErrorMessage && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {reviewDateErrorMessage}
            </p>
          )}
          <div className="govuk-date-input" id="reviewDate">
            <div className="govuk-date-input__item">
              <div className="govuk-form-group">
                <label
                  className="govuk-label govuk-date-input__label"
                  htmlFor="reviewDate"
                >
                  Day
                </label>
                <input
                  className={`govuk-input govuk-date-input__input govuk-input--width-2 ${reviewDateErrorMessage ? 'govuk-input--error' : ''}`}
                  id="reviewDate"
                  name="reivew-date-day"
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={reviewDate.dateDetails.day}
                  onChange={e => {
                    const { value } = e.target;
                    if (/^[0-9]*$/.test(value) && value.length <= 2) {
                      setReviewDate({
                        ...reviewDate,
                        dateDetails: { ...reviewDate.dateDetails, day: value },
                      });
                    }
                  }}
                />
              </div>
            </div>
            <div className="govuk-date-input__item">
              <div className="govuk-form-group">
                <label
                  className="govuk-label govuk-date-input__label"
                  htmlFor="review-date-month"
                >
                  Month
                </label>
                <input
                  className={`govuk-input govuk-date-input__input govuk-input--width-2 ${reviewDateErrorMessage ? 'govuk-input--error' : ''}`}
                  id="review-date-month"
                  name="review-date-month"
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={reviewDate.dateDetails.month}
                  onChange={e => {
                    const { value } = e.target;
                    if (/^[0-9]*$/.test(value) && value.length <= 2) {
                      setReviewDate({
                        ...reviewDate,
                        dateDetails: {
                          ...reviewDate.dateDetails,
                          month: value,
                        },
                      });
                    }
                  }}
                />
              </div>
            </div>
            <div className="govuk-date-input__item">
              <div className="govuk-form-group">
                <label
                  className="govuk-label govuk-date-input__label"
                  htmlFor="review-date-year"
                >
                  Year
                </label>
                <input
                  className={`govuk-input govuk-date-input__input govuk-input--width-4 ${reviewDateErrorMessage ? 'govuk-input--error' : ''}`}
                  id="review-date-year"
                  name="review-date-year"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={reviewDate.dateDetails.year}
                  onChange={e => {
                    const { value } = e.target;
                    if (/^[0-9]*$/.test(value) && value.length <= 4) {
                      setReviewDate({
                        ...reviewDate,
                        dateDetails: { ...reviewDate.dateDetails, year: value },
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <div className="govuk-form-group">
        <fieldset
          className="govuk-fieldset"
          aria-describedby="not-relevant-hint"
        >
          <div className="govuk-checkboxes" data-module="govuk-checkboxes">
            <div className="govuk-checkboxes__item">
              <input
                className="govuk-checkboxes__input"
                id="not-relevant"
                name="not-relevant"
                type="checkbox"
                checked={reviewDate.isNotRelevant}
                onChange={onClickingNotRelevant}
              />
              <label
                className="govuk-label govuk-checkboxes__label"
                htmlFor="not-relevant"
              >
                Not relevant
              </label>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default ReviewDate;
